import React from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { CalendarDays, Users, Mail } from "lucide-react";

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <Button
        variant="ghost"
        className="mb-4"
        onClick={() => navigate(-1)}
      >
        ← Back
      </Button>
      
      <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>
      
      <div className="space-y-6 text-left">
        <p className="text-sm text-gray-600">Effective Date: 2/16/25</p>
        <p className="text-sm text-gray-600">Last Updated: 2/16/25</p>
        <p className="text-sm text-gray-600">Version: 1.1</p>
        
        <section className="space-y-3">
          <h2 className="text-xl font-semibold">Definitions</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>"Service" refers to Alfie, the meeting scheduling application</li>
            <li>"User" refers to any individual who uses the Service</li>
            <li>"Personal Data" refers to any information relating to an identified or identifiable natural person</li>
            <li>"Google User Data" refers to any data accessed through Google APIs</li>
            <li>"We," "our," "us" refers to Alfie, a product developed by Moxi-Kaizen</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold">1. Introduction</h2>
          <p>Alfie ("we," "our," "us") is a product developed by Moxi-Kaizen, a company operating out of California. We are committed to protecting your privacy. This Privacy Policy describes how we collect, use, and share information about you when you use our services. This policy complies with Google's requirements for app verification and the Google API Services User Data Policy, including the Limited Use requirements.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold">2. Information We Collect</h2>
          <p>We may collect the following types of information:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Personal information (e.g., name, email address, phone number) when you sign up</li>
            <li>Usage data, such as interactions with the app and preferences</li>
            <li>Device and log information, including IP address, browser type, and operating system</li>
            <li>Cookies and tracking technologies to enhance user experience and track app performance</li>
            <li>Google User Data, when applicable, in accordance with Google's API Services User Data Policy</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold">3. Data Access & Permissions</h2>
          <p>Alfie allows users to enter a prompt to schedule a meeting with someone they know. Alfie checks the user's contact list and past and future invites to find that contact, then schedules the meeting and sends meeting invites.</p>
          
          <h3 className="text-lg font-semibold mt-6 mb-4">What Access Alfie Needs & Why</h3>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <CalendarDays className="h-6 w-6 text-purple-600" />
                <h4 className="font-semibold text-lg">Calendar Access</h4>
              </div>
              <div className="space-y-3">
                <p className="text-gray-700">We need calendar access to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>View your existing calendar events to find optimal meeting times</li>
                  <li>Create new calendar events when you schedule meetings</li>
                  <li>Send meeting invites to participants</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">Note: We cannot modify or delete your existing calendar events</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <Users className="h-6 w-6 text-purple-600" />
                <h4 className="font-semibold text-lg">Contacts Access</h4>
              </div>
              <div className="space-y-3">
                <p className="text-gray-700">We need contacts access to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>Find the right person when you want to schedule a meeting</li>
                  <li>Suggest email addresses as you type</li>
                  <li>Verify contact information</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">Note: We can only read your contacts. We cannot add, modify, or delete any contacts.</p>
              </div>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="h-6 w-6 text-purple-600" />
                <h4 className="font-semibold text-lg">Basic Profile Access</h4>
              </div>
              <div className="space-y-3">
                <p className="text-gray-700">We need profile access to:</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-600">
                  <li>Verify your identity when you sign in</li>
                  <li>Use your email address for meeting invitations</li>
                  <li>Display your name in the application</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">We only access basic profile information needed for the app to function.</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
              <p className="font-medium mb-2">Technical Details:</p>
              <p>For transparency, here are the specific Google API scopes we use:</p>
              <ul className="space-y-1 mt-2 font-mono text-xs">
                <li>https://www.googleapis.com/auth/calendar.events</li>
                <li>https://www.googleapis.com/auth/calendar.readonly</li>
                <li>https://www.googleapis.com/auth/contacts.readonly</li>
                <li>https://www.googleapis.com/auth/userinfo.email</li>
                <li>https://www.googleapis.com/auth/userinfo.profile</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold">4. How We Use Your Information</h2>
          <p>We use the collected information to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide, maintain, and improve our services.</li>
            <li>Personalize the user experience.</li>
            <li>Communicate with users regarding updates or support.</li>
            <li>Ensure compliance with legal obligations.</li>
            <li>Adhere to Google's Limited Use Policy for any Google user data processed through the app.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold">5. Sharing of Information</h2>
          <p>We do not sell or rent your personal data. However, we may share information with:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Service providers</strong> assisting with app functionality.</li>
            <li><strong>Legal authorities</strong> if required by law.</li>
            <li><strong>Third-party services</strong> only when necessary and in compliance with Google's API Services User Data Policy.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold">6. Data Security</h2>
          <p>We implement industry-standard security measures to protect your personal information. However, no online service is completely secure, and we encourage users to take precautions.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold">7. User Rights</h2>
          <p>Depending on your location, you may have rights regarding your personal data, such as:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Access to data we have collected.</li>
            <li>Request correction or deletion of personal data.</li>
            <li>Opt-out of data collection where applicable.</li>
          </ul>
          <p>To exercise these rights, contact us at support@meetalfie.ai</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold">8. Compliance with Google API Services User Data Policy</h2>
          <p>Our use and transfer of information received from Google APIs adhere to the Google API Services User Data Policy, including its Limited Use requirements.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-xl font-semibold">9. Contact Information</h2>
          <p>If you have questions about this Privacy Policy, contact us at support@meetalfie.ai</p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
